/**
 * SJ Build Your Own Bundle
 * - Step wise product selection, live summary, tier progress (e.g. 5% / 7% / 10%)
 * - "Secure Checkout": adds all items via /cart/add.js, applies the tier's
 *   discount code through /discount/CODE?redirect=..., then opens checkout/cart.
 * No app needed. Discount itself must exist in Shopify Admin > Discounts.
 */
(() => {
  if (window.__sjBundleBuilderReady) return;
  window.__sjBundleBuilderReady = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const storage = {
    get(key) {
      try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
    },
    remove(key) {
      try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
    },
  };

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) || 0;
    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? dollars + decimal + parts[1] : dollars;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  const decodeHtml = (s) => {
    const t = document.createElement('textarea');
    t.innerHTML = s || '';
    return t.value;
  };

  class BundleBuilder {
    constructor(el) {
      this.el = el;
      this.d = el.dataset;
      this.moneyFormat = decodeHtml(this.d.moneyFormat);
      this.minItems = Math.max(parseInt(this.d.minItems, 10) || 1, 1);
      this.maxItems = parseInt(this.d.maxItems, 10) || 0;
      const words = (this.d.labelItems || 'item|items').split('|');
      this.wordOne = words[0] || 'item';
      this.wordMany = words[1] || words[0] || 'items';
      this.storageKey = `sjbb:${window.location.pathname}:${this.d.sectionId}`;

      this.tiers = Array.from(el.querySelectorAll('[data-tier]'))
        .map((t) => ({
          el: t,
          qty: parseInt(t.dataset.tierQty, 10) || 0,
          pct: parseFloat(t.dataset.tierPct) || 0,
          code: t.dataset.tierCode || '',
        }))
        .filter((t) => t.qty > 0 && t.pct > 0)
        .sort((a, b) => a.qty - b.qty);

      /** @type {Map<string, {key:string, stepId:string, card:HTMLElement, variantId:string, qty:number}>} */
      this.items = new Map();
      this.order = [];

      this.summary = el.querySelector('[data-summary]');
      this.messageEl = el.querySelector('[data-message]');
      this.errorEl = el.querySelector('[data-error]');
      this.checkoutBtn = el.querySelector('[data-checkout]');

      this.positionTiers();
      this.bind();
      this.restore();
      this.render();
    }

    /* ---------- helpers ---------- */
    plural(n) { return n === 1 ? this.wordOne : this.wordMany; }

    fill(template, map) {
      return (template || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
    }

    cardKey(card) {
      const step = card.closest('[data-step]');
      return `${step.dataset.stepId}:${card.dataset.productId}`;
    }

    selectedOption(card) {
      const select = card.querySelector('[data-variant-select]');
      return select ? select.options[select.selectedIndex] : null;
    }

    optionFor(card, variantId) {
      const select = card.querySelector('[data-variant-select]');
      return select ? Array.from(select.options).find((o) => o.value === String(variantId)) : null;
    }

    totalQty() {
      let n = 0;
      this.items.forEach((i) => { n += i.qty; });
      return n;
    }

    stepQty(stepId) {
      let n = 0;
      this.items.forEach((i) => { if (i.stepId === stepId) n += i.qty; });
      return n;
    }

    stepMax(stepId) {
      const step = this.el.querySelector(`[data-step][data-step-id="${CSS.escape(stepId)}"]`);
      return step ? parseInt(step.dataset.stepMax, 10) || 0 : 0;
    }

    canAdd(stepId) {
      const sMax = this.stepMax(stepId);
      if (sMax > 0 && this.stepQty(stepId) >= sMax) {
        this.flash(this.fill(this.d.labelMaxStep, { n: sMax }));
        return false;
      }
      if (this.maxItems > 0 && this.totalQty() >= this.maxItems) {
        this.flash(this.fill(this.d.labelMaxTotal, { n: this.maxItems }));
        return false;
      }
      return true;
    }

    currentTier(count) {
      let cur = null;
      this.tiers.forEach((t) => { if (count >= t.qty) cur = t; });
      return cur;
    }

    nextTier(count) {
      return this.tiers.find((t) => t.qty > count) || null;
    }

    /* ---------- events ---------- */
    bind() {
      this.el.addEventListener('click', (e) => {
        const t = e.target;

        const toggle = t.closest('[data-step-toggle]');
        if (toggle) {
          const step = toggle.closest('[data-step]');
          const open = !step.classList.contains('is-open');
          step.classList.toggle('is-open', open);
          toggle.setAttribute('aria-expanded', String(open));
          return;
        }

        const card = t.closest('[data-card]');
        if (card) {
          if (t.closest('[data-card-add]')) return this.addCard(card);
          if (t.closest('[data-card-plus]')) return this.changeCard(card, +1);
          if (t.closest('[data-card-minus]')) return this.changeCard(card, -1);
          if (t.closest('[data-card-remove]')) return this.removeKey(this.cardKey(card));
        }

        const plus = t.closest('[data-item-plus]');
        if (plus) {
          const item = this.items.get(plus.dataset.itemPlus);
          if (item) this.changeCard(item.card, +1);
          return;
        }
        const minus = t.closest('[data-item-minus]');
        if (minus) {
          const item = this.items.get(minus.dataset.itemMinus);
          if (item) this.changeCard(item.card, -1);
          return;
        }
        const x = t.closest('[data-item-remove]');
        if (x) return this.removeKey(x.dataset.itemRemove);

        const jump = t.closest('[data-slot-jump]');
        if (jump) return this.jumpToStep(jump.dataset.slotJump);

        if (t.closest('[data-dock-toggle]')) return this.toggleDock();
        if (t.closest('[data-checkout]')) return this.checkout();
      });

      this.el.addEventListener('change', (e) => {
        const select = e.target.closest('[data-variant-select]');
        if (!select) return;
        const card = select.closest('[data-card]');
        const opt = this.selectedOption(card);
        if (opt) card.querySelectorAll('[data-card-price], [data-card-price-sel]').forEach((p) => {
          p.textContent = formatMoney(opt.dataset.price, this.moneyFormat);
        });
        const item = this.items.get(this.cardKey(card));
        if (item && opt) {
          item.variantId = opt.value;
          this.render();
        }
      });

      // Links like <a href="#bundle-builder"> (e.g. a banner "Start Now" button) scroll smoothly.
      document.addEventListener('click', (e) => {
        const a = e.target.closest(`a[href$="#${this.el.id}"]`);
        if (!a) return;
        const url = new URL(a.href, window.location.href);
        if (url.pathname !== window.location.pathname) return;
        e.preventDefault();
        this.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    addCard(card) {
      const key = this.cardKey(card);
      if (this.items.has(key)) return this.changeCard(card, +1);
      const opt = this.selectedOption(card);
      if (!opt || opt.dataset.available !== 'true') return;
      const stepId = card.closest('[data-step]').dataset.stepId;
      if (!this.canAdd(stepId)) return;
      this.items.set(key, { key, stepId, card, variantId: opt.value, qty: 1 });
      this.order.push(key);
      this.render();
    }

    changeCard(card, delta) {
      const key = this.cardKey(card);
      const item = this.items.get(key);
      if (!item) return delta > 0 ? this.addCard(card) : undefined;
      if (delta > 0 && !this.canAdd(item.stepId)) return;
      item.qty += delta;
      if (item.qty <= 0) return this.removeKey(key);
      this.render();
    }

    removeKey(key) {
      this.items.delete(key);
      this.order = this.order.filter((k) => k !== key);
      this.render();
    }

    jumpToStep(stepId) {
      const step = this.el.querySelector(`[data-step][data-step-id="${CSS.escape(stepId)}"]`);
      if (!step) return;
      step.classList.add('is-open');
      const head = step.querySelector('[data-step-toggle]');
      if (head) head.setAttribute('aria-expanded', 'true');
      this.summary.classList.remove('is-expanded');
      step.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    toggleDock() {
      const open = !this.summary.classList.contains('is-expanded');
      this.summary.classList.toggle('is-expanded', open);
      const btn = this.summary.querySelector('[data-dock-toggle]');
      if (btn) btn.setAttribute('aria-expanded', String(open));
    }

    flash(text) {
      if (!this.messageEl) return;
      this.messageEl.textContent = text;
      this.messageEl.classList.remove('is-success');
      clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => this.render(), 2600);
    }

    /* ---------- persistence ---------- */
    save() {
      if (this.d.remember !== 'true') return;
      const data = this.order.map((k) => {
        const i = this.items.get(k);
        return { k, v: i.variantId, q: i.qty };
      });
      if (data.length) storage.set(this.storageKey, data);
      else storage.remove(this.storageKey);
    }

    restore() {
      if (this.d.remember !== 'true') return;
      const data = storage.get(this.storageKey);
      if (!Array.isArray(data)) return;
      data.forEach(({ k, v, q }) => {
        const [stepId, productId] = String(k).split(':');
        const step = this.el.querySelector(`[data-step][data-step-id="${CSS.escape(stepId)}"]`);
        const card = step && step.querySelector(`[data-card][data-product-id="${CSS.escape(productId)}"]`);
        if (!card) return;
        const opt = this.optionFor(card, v);
        if (!opt || opt.dataset.available !== 'true') return;
        opt.selected = true;
        card.querySelectorAll('[data-card-price], [data-card-price-sel]').forEach((p) => {
          p.textContent = formatMoney(opt.dataset.price, this.moneyFormat);
        });
        this.items.set(k, { key: k, stepId, card, variantId: opt.value, qty: Math.max(1, parseInt(q, 10) || 1) });
        this.order.push(k);
      });
    }

    /* ---------- progress ---------- */
    positionTiers() {
      if (!this.tiers.length) return;
      const maxQty = this.tiers[this.tiers.length - 1].qty;
      this.tiers.forEach((t) => { t.el.style.left = `${(t.qty / maxQty) * 100}%`; });
    }

    /* ---------- render ---------- */
    render() {
      const count = this.totalQty();
      let subtotal = 0;
      let compareTotal = 0;

      // Cards
      this.el.querySelectorAll('[data-card]').forEach((card) => {
        const item = this.items.get(this.cardKey(card));
        const selected = Boolean(item);
        card.classList.toggle('is-selected', selected);
        const add = card.querySelector('[data-card-add]');
        const row = card.querySelector('[data-card-qtyrow]');
        const rm = card.querySelector('[data-card-remove]');
        if (add) add.hidden = selected;
        if (row) row.hidden = !selected;
        if (rm) rm.hidden = !selected;
        const qtyEl = card.querySelector('[data-card-qty]');
        if (qtyEl) qtyEl.textContent = selected ? item.qty : 0;
      });

      // Step counters
      this.el.querySelectorAll('[data-step]').forEach((step) => {
        const n = this.stepQty(step.dataset.stepId);
        const badge = step.querySelector('[data-step-count]');
        if (badge) {
          badge.hidden = n === 0;
          badge.textContent = n;
        }
      });

      // Slots
      this.el.querySelectorAll('[data-slot]').forEach((slot) => {
        const stepId = slot.dataset.slot;
        const wrap = slot.querySelector('[data-slot-items]');
        const keys = this.order.filter((k) => this.items.get(k).stepId === stepId);
        wrap.innerHTML = '';
        if (!keys.length) {
          const empty = document.createElement('button');
          empty.type = 'button';
          empty.className = 'sjbb-slot__empty';
          empty.dataset.slotJump = stepId;
          empty.textContent = this.d.labelEmptySlot || 'Add product to this step';
          wrap.appendChild(empty);
          return;
        }
        keys.forEach((k) => {
          const item = this.items.get(k);
          const opt = this.optionFor(item.card, item.variantId);
          if (!opt) return;
          const row = document.createElement('div');
          row.className = 'sjbb-item';

          const img = document.createElement('img');
          img.className = 'sjbb-item__img';
          img.alt = '';
          img.loading = 'lazy';
          if (opt.dataset.image) img.src = opt.dataset.image;

          const name = document.createElement('div');
          name.className = 'sjbb-item__name';
          name.textContent = item.card.dataset.productTitle ? decodeHtml(item.card.dataset.productTitle) : '';
          if (opt.dataset.title) {
            const v = document.createElement('span');
            v.className = 'sjbb-item__var';
            v.textContent = decodeHtml(opt.dataset.title);
            name.appendChild(v);
          }

          const qty = document.createElement('div');
          qty.className = 'sjbb-qty';
          qty.innerHTML =
            `<button type="button" class="sjbb-qty__btn sjbb-qty__btn--ghost" aria-label="−" data-item-minus="${k}">−</button>` +
            `<span class="sjbb-qty__val">${item.qty}</span>` +
            `<button type="button" class="sjbb-qty__btn sjbb-qty__btn--ghost" aria-label="+" data-item-plus="${k}">+</button>`;

          const x = document.createElement('button');
          x.type = 'button';
          x.className = 'sjbb-item__x';
          x.setAttribute('aria-label', 'Remove');
          x.dataset.itemRemove = k;
          x.textContent = '×';

          row.append(img, name, qty, x);
          wrap.appendChild(row);
        });
      });

      // Totals
      this.items.forEach((item) => {
        const opt = this.optionFor(item.card, item.variantId);
        if (!opt) return;
        const price = parseInt(opt.dataset.price, 10) || 0;
        subtotal += price * item.qty;
        compareTotal += price * item.qty;
      });

      const tier = this.currentTier(count);
      const next = this.nextTier(count);
      const discount = tier ? Math.round((subtotal * tier.pct) / 100) : 0;
      const total = subtotal - discount;

      const totalEl = this.el.querySelector('[data-total]');
      const cmpEl = this.el.querySelector('[data-total-compare]');
      const saveEl = this.el.querySelector('[data-saving]');
      if (totalEl) totalEl.textContent = formatMoney(total, this.moneyFormat);
      if (cmpEl) {
        cmpEl.hidden = discount <= 0;
        cmpEl.textContent = formatMoney(compareTotal, this.moneyFormat);
      }
      if (saveEl) {
        saveEl.hidden = discount <= 0;
        saveEl.textContent = discount > 0 ? `You save ${formatMoney(discount, this.moneyFormat)} (${tier.pct}%)` : '';
      }
      const dockCount = this.el.querySelector('[data-dock-count]');
      const dockTotal = this.el.querySelector('[data-dock-total]');
      if (dockCount) dockCount.textContent = `(${count})`;
      if (dockTotal) dockTotal.textContent = formatMoney(total, this.moneyFormat);

      // Progress
      if (this.tiers.length) {
        const maxQty = this.tiers[this.tiers.length - 1].qty;
        const fill = this.el.querySelector('[data-progress-fill]');
        if (fill) fill.style.width = `${Math.min(count / maxQty, 1) * 100}%`;
        this.tiers.forEach((t) => t.el.classList.toggle('is-on', count >= t.qty));
      }

      // Message
      if (this.messageEl) {
        let text = '';
        let ok = false;
        if (this.tiers.length) {
          if (!tier && next) {
            const n = next.qty - count;
            text = this.fill(this.d.labelUnlock, { n, items: this.plural(n), pct: next.pct });
          } else if (tier && next) {
            const n = next.qty - count;
            text = this.fill(this.d.labelUnlockedNext, { cur: tier.pct, n, items: this.plural(n), pct: next.pct });
            ok = true;
          } else if (tier) {
            text = this.fill(this.d.labelUnlocked, { cur: tier.pct });
            ok = true;
          }
        }
        if (count > 0 && count < this.minItems) {
          const n = this.minItems;
          text = this.fill(this.d.labelMin, { n, items: this.plural(n) });
          ok = false;
        }
        this.messageEl.textContent = text;
        this.messageEl.classList.toggle('is-success', ok);
      }

      // Required steps + min items gate
      const missingRequired = Array.from(this.el.querySelectorAll('[data-step][data-step-required="true"]'))
        .some((s) => this.stepQty(s.dataset.stepId) === 0);
      if (this.checkoutBtn && this.checkoutBtn.getAttribute('aria-busy') !== 'true') {
        this.checkoutBtn.disabled = count < this.minItems || missingRequired;
      }
      if (this.errorEl) this.errorEl.hidden = true;

      this.save();
    }

    /* ---------- checkout ---------- */
    async checkout() {
      const count = this.totalQty();
      if (!count || this.checkoutBtn.disabled) return;
      this.checkoutBtn.setAttribute('aria-busy', 'true');
      this.checkoutBtn.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;

      const bundleId = `BYOB-${Date.now().toString(36).toUpperCase()}`;
      const items = this.order.map((k) => {
        const i = this.items.get(k);
        const line = { id: Number(i.variantId), quantity: i.qty };
        if (this.d.tagItems === 'true') line.properties = { _bundle: bundleId };
        return line;
      });

      try {
        if (this.d.replaceCart === 'true') {
          await fetch(`${root()}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
        }
        const res = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || '');

        const tier = this.currentTier(count);
        const dest = this.d.destination === 'cart' ? `${root()}cart` : '/checkout';
        storage.remove(this.storageKey);

        if (tier && tier.code) {
          window.location.href = `${root()}discount/${encodeURIComponent(tier.code)}?redirect=${encodeURIComponent(dest)}`;
        } else {
          window.location.href = dest;
        }
      } catch (err) {
        this.checkoutBtn.removeAttribute('aria-busy');
        this.checkoutBtn.disabled = false;
        if (this.errorEl) {
          this.errorEl.textContent = (err && err.message) || this.d.labelError;
          this.errorEl.hidden = false;
        }
      }
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-sjbb]').forEach((el) => {
      if (el.__sjbb) return;
      el.__sjbb = new BundleBuilder(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
